import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, FileText, AlertCircle, CheckCircle, LogOut, User, Camera, HelpCircle, Scale } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function Dashboard() {
  const [stats, setStats] = useState({
    asignaciones: 0,
    multas: 0,
    pendientes: 0,
    identificadas: 0,
  });
  const navigate = useNavigate();
  const { usuario, loading, logout } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!usuario?.empresa) {
      navigate('/');
      return;
    }
    cargarEstadisticas();
  }, [loading, usuario]);

  const cargarEstadisticas = async () => {
    if (!usuario?.empresa?.id) return;
    try {
      const [asignaciones, multas] = await Promise.all([
        api.get(`/asignaciones?empresa_id=${usuario.empresa.id}`),
        api.get(`/multas?empresa_id=${usuario.empresa.id}`)
      ]);

      const multasData = multas.data.data || [];

      setStats({
        asignaciones: asignaciones.data.data?.length || 0,
        multas: multasData.length,
        pendientes: multasData.filter(m => m.estado === 'pendiente_identificacion').length,
        identificadas: multasData.filter(m => m.estado === 'conductor_identificado').length,
      });
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      // El interceptor de api.js maneja el 401 automáticamente
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-lg shadow-soft border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-display">
              {usuario.empresa?.nombre || 'Dashboard'}
            </h1>
            <p className="text-sm text-slate-600">
              Sistema de Gestión de Multas
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/recursos")} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"><Scale className="w-5 h-5" />Recursos</button>
            <button onClick={() => navigate("/ayuda")} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"><HelpCircle className="w-5 h-5" />Ayuda</button>
            <button onClick={() => navigate("/profile")} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"><User className="w-5 h-5" />Perfil</button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"><LogOut className="w-5 h-5" />Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Car}
            title="Asignaciones Activas"
            value={stats.asignaciones}
            color="primary"
          />
          <StatCard
            icon={FileText}
            title="Total Multas"
            value={stats.multas}
            color="violet"
          />
          <StatCard
            icon={CheckCircle}
            title="Identificadas"
            value={stats.identificadas}
            color="emerald"
          />
          <StatCard
            icon={AlertCircle}
            title="Pendientes"
            value={stats.pendientes}
            color="amber"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ActionCard
            title="Gestionar Asignaciones"
            description="Crear y administrar asignaciones temporales de vehículos"
            buttonText="Ir a Asignaciones"
            onClick={() => navigate('/asignaciones')}
            icon={Car}
            color="primary"
          />
          <ActionCard
            title="Ver Multas"
            description="Consultar y gestionar multas recibidas"
            buttonText="Ver Multas"
            onClick={() => navigate('/multas')}
            icon={FileText}
            color="violet"
          />
          <ActionCard
            title="Recursos Legales"
            description="Solicita recursos para tus multas con fuentes legales verificadas"
            buttonText="Gestionar Recursos"
            onClick={() => navigate('/recursos')}
            icon={Scale}
            color="emerald"
          />
          <ActionCard
            title="Subir Multa (OCR)"
            description="Sube una foto o PDF de una multa física y extrae los datos automáticamente"
            buttonText="Subir Multa"
            onClick={() => navigate('/subir-multa')}
            icon={Camera}
            color="accent"
          />
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, title, value, color }) {
  const colors = {
    primary: 'bg-primary-100 text-primary-600',
    violet: 'bg-violet-100 text-violet-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
  };

  return (
    <div className="stat-card border border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <h3 className="text-slate-600 text-sm font-medium mb-1">{title}</h3>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function ActionCard({ title, description, buttonText, onClick, icon: Icon, color }) {
  const buttonColors = {
    primary: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 hover:shadow-glow',
    violet: 'bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700',
    emerald: 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700',
    accent: 'bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 hover:shadow-glow-accent',
  };
  const iconColors = {
    primary: 'bg-primary-100 text-primary-600',
    violet: 'bg-violet-100 text-violet-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    accent: 'bg-accent-100 text-accent-600',
  };

  return (
    <div className="stat-card border border-slate-200">
      <div className="flex items-start gap-4 mb-4">
        <div className={`p-3 rounded-xl ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
          <p className="text-slate-600 text-sm">{description}</p>
        </div>
      </div>
      <button
        onClick={onClick}
        className={`w-full ${buttonColors[color]} text-white py-2.5 px-4 rounded-xl font-medium transition-all transform hover:-translate-y-0.5`}
      >
        {buttonText}
      </button>
    </div>
  );
}

export default Dashboard;
